import { useModals } from '@/hooks/ModalManager';
import useSubmitForm from '@/modules/WebForm/hooks/useSubmitForm';
import { useSyncState } from '@/modules/WebForm/hooks/useSyncState';
import { Button, Flex } from '@chakra-ui/react';
import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import useFindQuestion from '../hooks/useFindQuestion';
import useSections from '../hooks/useSections';
import { useWebFormProps } from '../hooks/useWebFormProps';

const Pagination: React.FC = () => {
  const { t } = useTranslation();
  const { onSubmit } = useWebFormProps();
  const { findQuestion } = useFindQuestion();
  const modals = useModals();
  const { sections, currentSection, setSection } = useSections();
  const { isPushing } = useSyncState();

  const idx = React.useMemo(
    () => sections.findIndex((el) => el.id === currentSection),
    [currentSection, sections],
  );

  const setSectByOffset = React.useCallback(
    (next: number) => () => {
      const nextIdx = idx + next;
      const nextSect = sections[nextIdx];
      setSection(nextSect.id);
    },
    [idx, sections, setSection],
  );

  const submitForm = useSubmitForm((result) => {
    const firstError = Object.keys(result.errors)?.[0];

    const firstErrorQuestion = findQuestion(firstError);
    if (onSubmit) {
      onSubmit(result);
      if (firstErrorQuestion?.sectionId) {
        setSection(firstErrorQuestion.sectionId);
      }
      return;
    }

    if (!result.isValid) {
      modals.alert({
        title: 'Atenção',
        body: 'Responda todos os campos obrigatórios!',
        onConfirm: () => {
          if (firstErrorQuestion?.sectionId) {
            setSection(firstErrorQuestion.sectionId);
          }
        },
      });
    }
  });

  const isLastPage = idx + 1 === sections.length;

  return (
    <Flex w="full" flexDir="row" justifyContent={idx - 1 !== -1 ? 'space-between' : 'flex-end'}>
      {idx - 1 !== -1 && (
        <Button
          size="sm"
          disabled={idx - 1 === -1}
          colorScheme="brand"
          leftIcon={<FiChevronLeft />}
          onClick={setSectByOffset(-1)}
        >
          {t('pagination.previous_page')}
        </Button>
      )}
      {isLastPage ? (
        <Button
          size="sm"
          colorScheme="brand"
          rightIcon={<FiChevronRight />}
          onClick={submitForm}
          disabled={isPushing}
        >
          {t('buttons.finish')}
        </Button>
      ) : (
        <Button
          size="sm"
          disabled={idx + 1 === sections.length || isPushing}
          colorScheme="brand"
          rightIcon={<FiChevronRight />}
          onClick={setSectByOffset(1)}
        >
          {t('pagination.next_page')}
        </Button>
      )}
    </Flex>
  );
};

export default Pagination;
